// Pure functions backing the calendar grid. No DOM, no state.

export interface CalendarCell {
  date: Date;
  /** YYYY-MM-DD */
  iso: string;
  /** True if `date` falls in `month`/`year`; false for leading/trailing cells. */
  inMonth: boolean;
}

export function isoFor(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Six-row month grid (42 cells) starting on Sunday. Leading and trailing
 * cells fill from the prior/next month and are flagged via `inMonth`.
 */
export function buildMonthGrid(year: number, month: number): CalendarCell[] {
  const first = new Date(year, month, 1);
  const startDay = first.getDay(); // 0..6
  const cells: CalendarCell[] = [];
  const start = new Date(year, month, 1 - startDay);
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({
      date: d,
      iso: isoFor(d),
      inMonth: d.getMonth() === month,
    });
  }
  return cells;
}

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export interface MonthShift {
  year: number;
  month: number;
}

export function shiftMonth(state: MonthShift, delta: number): MonthShift {
  const total = state.year * 12 + state.month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}
